import * as React from 'react';
import * as _ from 'underscore';

/**
 * This file contains a provider class to make editable content behave like "radio buttons":
 * ie. at most one of multiple inline-editable items can be edited at any one time, and starting to one
 * causes the others to "pop back" into their readonly state.
 * 
 * From a consumer's perspective, they wrap the relevant elements in a <RadioEditProvider/> (analogous to XML <radiogroup/>)
 * and then consume getters and setters for whether they are being edited via the `useRadioEditcontext` hook. 
 * The provider contains logic to ensure that only one can be active at any point in time.
 */

/**
 * 
 */
export type RadioEditState = {
  readonly isEditing: boolean;
  readonly isReadonly: boolean;
  readonly setIsEditing: (shouldEdit: boolean) => void;
};

export const RadioEditProvider = (props: {isReadonly: boolean, children: React.ReactNode}) => (
  <RadioContext.Provider
    value={useRadioEditProviderState(props.isReadonly)}
    children={props.children}/>);

/**
 * React hook which returns the editing status linked to the nearest parent <RadioEditProvider/>
 * isReadonly indicates edits are disallowed.
 * isEditing + setIsEditing are the value and setter for whether this particular component is currently editable.
 */
export const useRadioEditContext = (): RadioEditState => {
  // Hook up to nearest context
  const {isReadonly, editCount, addEdit} = React.useContext(RadioContext);

  // We store when we started editing, so if another component in the context starts editing, we no longer have permission to do so.
  const [lastEditingTime, setLastEditingTime] = React.useState(-1);
  const isEditing = lastEditingTime === editCount;

  // Callback to toggle whether we are editing.
  const setIsEditing = (shouldEdit: boolean): void => {
    if(isReadonly) { return; }
    if(shouldEdit) {
      // We will be editing in the next time slot, as soon as context finishes its update
      setLastEditingTime(editCount + 1);
    }
    addEdit();
  };

  return {
    isReadonly,
    setIsEditing,
    isEditing,
  };
};

// Underlying methods, should not be exported/consumed outside of this file.
type RadioEditProviderState = {
  readonly isReadonly: boolean;
  readonly editCount: number;
  readonly addEdit: () => void;
};

const useRadioEditProviderState = (isReadonly = false): RadioEditProviderState => {
  const [editCount, setEditCount] = React.useState(0);
  const addEdit = () => setEditCount(editCount + 1);

  return {
    isReadonly,
    editCount,
    addEdit
  };
};

const RadioContext = React.createContext<RadioEditProviderState>({isReadonly: false, editCount: 0, addEdit: _.noop});
